/**
 * seatLayout.ts —— 席位布局定位算法（第 5 轮 P0 重做）
 *
 * ## 为什么要重写
 *
 * 改前 `Table.seatPositions(n)` 是一条**与人数、容器、卡片尺寸全部无关**的固定百分比椭圆：
 * 半径恒为 43% / 40%，卡片却是 px 尺寸（148×120 / 116×102）。于是
 *   - 人数越多，弧长不变、卡片总宽线性增长 → 10 人桌必然重叠（xl/lg 实测 4 对）；
 *   - 「我」被 CSS 摘去贴底、对手仍吃极坐标 → 底部弧上的对手压在「我」身上（min360/sm 实测 2–6 对）；
 *   - 中心带、赢率浮层这些「受保护 / 遮挡源」根本不进入坐标计算 → 18 组对手可见比 < 1，最低 0。
 * 这类缺陷用 `@media` 微调半径是治不好的：断点是**离散的**，而人数 × 容器高度 × 卡片档位
 * 是**连续的三维空间**，任何一组魔法数字都只能压住一个格子。
 *
 * ## 本模块的口径
 *
 * 纯函数、无 DOM、可单测。输入是**实测几何**（容器内容盒、我的卡片盒、受保护中心带、
 * 外部浮层矩形、毛毡圆角），输出是每个对手的中心点与档位；调用方（Table.tsx）只负责测量与落 style。
 *
 * 算法本身是「带障碍物的轨道装箱」：
 *   1. **档位（density）阶梯**：full → compact → row → micro，卡片盒逐级变小、信息逐级收敛。
 *      先按信息量最全的档位试算，装不下才降档 —— 于是「人数感知」与「容器感知」是同一个判据。
 *   2. **区域模型**：上排（可 1–4 行）+ 左列 + 右列。「我」永远独占底部中央，
 *      并以「卡片盒 + 安全间距」构成**排他矩形**；受保护中心带、外部浮层同样是障碍矩形。
 *   3. **区间演算**：每一排的可用 x 区间 = 轨道全长减去所有障碍物在该排 y 带上的投影（含间距膨胀）；
 *      每一列的可用 y 区间同理（并把已使用的上排整行当作障碍）。容量 = Σ⌊区间长 / 步长⌋+1。
 *      因此「有障碍就少放、没障碍就多放」是几何推导出来的，不是写死的。
 *   4. **分配**：优先给上排 ⌈k/2⌉（不超过其容量），余量左右均分，仍有余量回填上排后续行。
 *   5. **圆角贴合**：毛毡是 `border-radius: 200px/140px` 的椭圆角。上排边缘位与侧列端点位
 *      按圆角边界下压 / 内收，位移量再被硬约束（障碍间距、盒内）夹紧 —— 视觉上的弧线是
 *      几何算出来的，不是手调的。
 *   6. **窄屏网格**：`planGrid` 用同一套档位阶梯挑「列数 × 行高」，硬约束是
 *      「行数 × 行高 + 间隙 ≤ 可用高度」，即**不滚动就放得下**（滚动会把对手滚到裁剪区外，
 *      QA 口径下等于不可见，也给了「滚动绕过」的坏路径）。
 *
 * 验收由 `validateRing` 统一判定：越界 / 两两重叠 / 侵入我的排他区 / 侵入中心带 / 侵入浮层。
 */

export interface Size {
  w: number;
  h: number;
}
export interface Point {
  x: number;
  y: number;
}
export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** 席位信息密度档位（同时驱动 CSS：`.felt--seats-<key>`）。 */
export type SeatDensity = 'full' | 'compact' | 'row' | 'micro';

export interface DensitySpec {
  key: SeatDensity;
  /** 环形布局下的卡片标称盒；CSS 用 `--seat-w/--seat-h` 锁死，实测必须等于标称值。 */
  ring: Size;
  /** 网格布局下的最小列宽、固定行高与网格间距。 */
  grid: { minColW: number; rowH: number; gap: number };
  /** 是否展示 HUD（V/P/A）。 */
  hud: boolean;
}

/**
 * 档位参数表（唯一的数字来源，改这里就等于改布局策略）。
 *
 * 数值来自本地预览实测并留了 2–4px 余量：
 *   - full 148×120：底牌 26×36 + 名字行 + 筹码行 + HUD，本街投入内联在筹码行（不长高）；
 *   - compact 116×120：去掉 HUD（−18），本街投入独占一行（+18）；
 *   - row 116×52：迷你牌面 20×28 + 两行 14px 文字 + 独占一行的本街投入；
 *   - micro 96×24：单行 14px 文字。
 * 网格布局下宽度由列宽给定，只需要「最小列宽 + 固定行高 + 网格间距」。
 */
export const DENSITIES: readonly DensitySpec[] = [
  { key: 'full', ring: { w: 148, h: 120 }, grid: { minColW: 150, rowH: 124, gap: 8 }, hud: true },
  { key: 'compact', ring: { w: 116, h: 120 }, grid: { minColW: 116, rowH: 124, gap: 8 }, hud: false },
  { key: 'row', ring: { w: 116, h: 52 }, grid: { minColW: 98, rowH: 52, gap: 6 }, hud: false },
  { key: 'micro', ring: { w: 96, h: 24 }, grid: { minColW: 84, rowH: 24, gap: 4 }, hud: false },
];

/**
 * 竖屏（stack）专用密度档表（Round21 §4.2.1 定稿 S0~S4）。
 *
 * 竖屏手机高度 > 宽度：改用「椭圆整周环布」把对手分布到左列 + 顶弧 + 右列，用满高度而非
 * 只用一条横带（旧 planGrid），是 9/10 人放得下且不重叠的根本。pod 盒比宽屏 oval 的
 * `DENSITIES` 更小，于是同样的椭圆周长上能放更多席位：
 *   - S0 full 78×96：头像 + 昵称（13px，5 全角 + 后缀）+ 筹码 + HUD（2~4 人）；
 *   - S1 compact 72×88：隐藏 HUD、昵称 12px 4 全角 + 后缀（5~6 人）；
 *   - S2 row 64×76：昵称 12px 3 全角 + 后缀，pod 内槽 P4 牌背小徽（7~8 人，宽 60→64）；
 *   - S3 micro 68×60：**宽而扁**，宽 +12 / 高 −4，内嵌动作条 ≥60px 可用宽（9~10 人）；
 *   - S4 micro/squeeze 64×52：10 人 + 浮层展开 / 320 宽兜底，配合 C4 收敛（K=2）。
 *
 * Round21 关键取舍（§4.2.1）：
 *   - S3「宽而扁」68×60——容量瓶颈在侧列纵向（3·h+2·gap≤214），宽度有余，把 56→68 换来
 *     ① AC-D6「10 人 pod 宽 ≥66」；② 内嵌动作条放得下「加注 497」；③ 昵称 3 全角 + 后缀不截断。
 *   - 旧三档极窄兜底（54×48 / 52×34 / 50×24）作废：50×24 只剩一行，动作条/牌背无处放，
 *     必然触发 C4 收敛。用**单一 S4（64×52）+ C4**替代「无限缩小 pod」。
 *
 * 复用 SeatDensity 键（full/compact/row/micro）→ CSS `.felt--seats-*` 与 `.seat--*` 无需新增分支，
 * 卡片实测盒由算法下发的 `--seat-w`/`--seat-h` 锁死为下表标称值。grid 字段在环布下不参与运算，
 * 仅为类型完备保留（stack 不再走 planGrid）。
 */
export const DENSITIES_PORTRAIT: readonly DensitySpec[] = [
  { key: 'full', ring: { w: 78, h: 96 }, grid: { minColW: 78, rowH: 96, gap: 8 }, hud: true },
  { key: 'compact', ring: { w: 72, h: 88 }, grid: { minColW: 72, rowH: 88, gap: 8 }, hud: false },
  /* S2 row：宽 60→64（§4.2.1），侧列 R_free 14、pod 内牌背小徽承载。 */
  { key: 'row', ring: { w: 64, h: 76 }, grid: { minColW: 64, rowH: 76, gap: 8 }, hud: false },
  /* S3 micro：56×64 → 68×60（宽 +12 / 高 −4），本方案最关键取舍——宽而扁换 10 人 pod ≥66 + 内嵌动作条。 */
  { key: 'micro', ring: { w: 68, h: 60 }, grid: { minColW: 68, rowH: 60, gap: 8 }, hud: false },
  /**
   * S4 squeeze（PRD §4.3.1 边界 / §4.2.1）：10 人 + 浮层展开 / 320 宽最不利包络下的唯一兜底档。
   * 替换旧三档极窄兜底（54×48 / 52×34 / 50×24）——只保留**单一 64×52**，配合 C4 可见性收敛
   * （只保留 K=2 席位完整动作，其余退化为 4px 语义色点）承接极密场景。沿用 `key:'micro'` 复用
   * `.felt--seats-micro` / `.seat--micro` 的 CSS，卡片实测盒由算法下发的 `--seat-w`/`--seat-h` 锁死。
   * 只有前四档全部 `validateRing` 不通过时才会命中，故不影响常规机型档位选择。
   */
  { key: 'micro', ring: { w: 64, h: 52 }, grid: { minColW: 64, rowH: 52, gap: 6 }, hud: false },
  /**
   * 几何兜底档（设计 §4.3.1 R_free 表「54 squeeze 兜底」）：≤320 宽极窄屏 9~10 人，S4（64×52）
   * 侧列 R_free 归零仍装不下时的**最后一档**——pod 宽压到 54（R_free 表基准），保证零重叠硬不变量
   * （`assertNoOverlap`）在设计支持范围（≥360）之外的极窄机型上也**永不破**。§4.2.1 的 S4=64×52 仍是
   * 「有名义档」，本档只在 S4 亦 `validateRing` 不过时命中，标签仍走 C4（K=2）收敛，不改承载语义。
   * 沿用 `key:'micro'` 复用 `.felt--seats-micro` / `.seat--micro`，卡片盒由 `--seat-w`/`--seat-h` 锁死。
   */
  { key: 'micro', ring: { w: 54, h: 48 }, grid: { minColW: 54, rowH: 48, gap: 6 }, hud: false },
];

const specFrom = (table: readonly DensitySpec[], key: SeatDensity): DensitySpec =>
  table.find((d) => d.key === key)!;

const spec = (key: SeatDensity): DensitySpec => specFrom(DENSITIES, key);

export interface RingInput {
  /** 席位层内容盒（= 毛毡 padding box）。 */
  box: Size;
  /** 对手数量（不含「我」）。 */
  count: number;
  /** 「我」的席位卡实测盒。 */
  me: Size;
  /** 受保护中心带（公共牌 + 底池）：层坐标，水平以盒中线对称。 */
  band?: { top: number; bottom: number; halfWidth: number } | null;
  /** 需要避让的外部浮层（层坐标矩形），如 lg 断点的赢率浮层。 */
  blockers?: readonly Rect[];
  /** 毛毡圆角（padding box 内的椭圆角半径），用于贴合弧线。 */
  shape?: { rx: number; ry: number } | null;
  /** 席位之间以及席位与障碍物之间的最小安全间距，缺省 8。 */
  gap?: number;
  /** 席位与内容盒边缘的最小内缩，缺省 8。 */
  edge?: number;
  /** 指定档位（诊断 / 单测用）；缺省按阶梯自动挑选。 */
  density?: SeatDensity;
  /**
   * 密度档阶梯（缺省 `DENSITIES` 宽屏档）。竖屏 stack 传 `DENSITIES_PORTRAIT`，
   * 让 planRing 用更小的 pod 盒在竖向椭圆上放下更多席位（不改宽屏 oval，避免回归）。
   */
  densities?: readonly DensitySpec[];
}

export interface RingPlan {
  density: SeatDensity;
  /** 该档位的卡片标称盒。 */
  card: Size;
  /** 「我」的中心点。 */
  me: Point;
  /** 对手中心点，顺时针：左列自下而上 → 上排自左向右 → 右列自上而下。 */
  seats: Point[];
  /** 是否在不压缩间距的前提下放得下。 */
  feasible: boolean;
  /** 诊断用：各区域容量与实际分配。 */
  diag: {
    topRows: number;
    capTop: number[];
    capLeft: number;
    capRight: number;
    used: { top: number[]; left: number; right: number };
    total: number;
  };
}

type Interval = [number, number];

const EPS = 0.01;

function subtract(list: readonly Interval[], hole: Interval): Interval[] {
  const out: Interval[] = [];
  for (const [a, b] of list) {
    const [ha, hb] = hole;
    if (hb <= a + EPS || ha >= b - EPS) {
      out.push([a, b]);
      continue;
    }
    if (ha > a + EPS) out.push([a, Math.min(b, ha)]);
    if (hb < b - EPS) out.push([Math.max(a, hb), b]);
  }
  return out.filter(([a, b]) => b - a >= -EPS);
}

function subtractAll(list: readonly Interval[], holes: readonly Interval[]): Interval[] {
  let cur = [...list];
  for (const h of holes) cur = subtract(cur, h);
  return cur;
}

/** 步长为 step 的轨道上，长度 len 的区间能放几个中心点。 */
function capacityOf(ivs: readonly Interval[], step: number): number {
  return ivs.reduce((s, [a, b]) => (b - a < -EPS ? s : s + Math.floor((b - a + EPS) / step) + 1), 0);
}

/** 在单个区间内均匀铺 n 个中心点（n=1 时取中点）。 */
function spreadOne([a, b]: Interval, n: number): number[] {
  if (n <= 0) return [];
  if (n === 1) return [(a + b) / 2];
  const s = (b - a) / (n - 1);
  return Array.from({ length: n }, (_, i) => a + i * s);
}

/** 把 n 个位置按容量分配到多个区间（容量大的先装满），返回升序中心坐标。 */
function allocate(ivs: readonly Interval[], n: number, step: number): number[] {
  if (n <= 0 || ivs.length === 0) return [];
  const caps = ivs.map((iv) => capacityOf([iv], step));
  const alloc = new Array(ivs.length).fill(0);
  const order = ivs.map((_, i) => i).sort((x, y) => caps[y] - caps[x] || x - y);
  let left = n;
  for (const i of order) {
    const take = Math.min(caps[i], left);
    alloc[i] = take;
    left -= take;
  }
  if (left > 0) alloc[order[0]] += left; // 超容兜底：压缩间距而不是丢席位
  const out: number[] = [];
  ivs.forEach((iv, i) => out.push(...spreadOne(iv, alloc[i])));
  return out.sort((a, b) => a - b);
}

function inter(a: Rect, b: Rect): boolean {
  return a.left < b.right - EPS && a.right > b.left + EPS && a.top < b.bottom - EPS && a.bottom > b.top + EPS;
}

/**
 * 圆角边界：给定水平位置 px，返回毛毡内容盒上边界允许的最小 y（下边界对称）。
 * 圆角是 rx×ry 的椭圆角，px 落在直边段时返回 0。
 */
function cornerTop(px: number, W: number, shape: { rx: number; ry: number }): number {
  const { rx, ry } = shape;
  if (rx <= 0 || ry <= 0) return 0;
  const d = px < rx ? rx - px : px > W - rx ? px - (W - rx) : 0;
  if (d <= 0) return 0;
  const t = Math.min(1, d / rx);
  return ry - ry * Math.sqrt(Math.max(0, 1 - t * t));
}

/** 圆角边界：给定垂直位置 py，返回左边界允许的最小 x（右边界对称）。 */
function cornerLeft(py: number, H: number, shape: { rx: number; ry: number }): number {
  const { rx, ry } = shape;
  if (rx <= 0 || ry <= 0) return 0;
  const d = py < ry ? ry - py : py > H - ry ? py - (H - ry) : 0;
  if (d <= 0) return 0;
  const t = Math.min(1, d / ry);
  return rx - rx * Math.sqrt(Math.max(0, 1 - t * t));
}

function planRingWith(inp: RingInput, sp: DensitySpec): RingPlan {
  const gap = inp.gap ?? 8;
  const edge = inp.edge ?? 8;
  const W = inp.box.w;
  const H = inp.box.h;
  const card = sp.ring;
  const hw = card.w / 2;
  const hh = card.h / 2;
  const cx = W / 2;
  const stepX = card.w + gap;
  const stepY = card.h + gap;

  // 「我」：底部居中，整卡贴内容盒下边缘（留 edge）。
  const meH = Math.min(inp.me.h, Math.max(0, H - 2 * edge));
  const meY = H - edge - meH / 2;
  const mePos: Point = { x: cx, y: meY };
  /** 「我」的排他矩形：卡片盒向下吃到盒底（下方没有可用轨道）。 */
  const meRect: Rect = {
    left: cx - inp.me.w / 2,
    right: cx + inp.me.w / 2,
    top: meY - meH / 2,
    bottom: H,
  };

  const obstacles: Rect[] = [meRect];
  if (inp.band) {
    obstacles.push({
      left: cx - inp.band.halfWidth,
      right: cx + inp.band.halfWidth,
      top: inp.band.top,
      bottom: inp.band.bottom,
    });
  }
  for (const b of inp.blockers ?? []) obstacles.push(b);

  const xMin = edge + hw;
  const xMax = W - edge - hw;
  const yMin = edge + hh;
  const yMax = H - edge - hh;

  /** 障碍物在某条水平带上的 x 投影（含间距膨胀）。 */
  const holesForRow = (y: number): Interval[] =>
    obstacles
      .filter((o) => o.top < y + hh + gap - EPS && o.bottom > y - hh - gap + EPS)
      .map((o) => [o.left - hw - gap, o.right + hw + gap] as Interval);

  /** 障碍物在某条垂直带上的 y 投影（含间距膨胀）。 */
  const holesForCol = (x: number): Interval[] =>
    obstacles
      .filter((o) => o.left < x + hw + gap - EPS && o.right > x - hw - gap + EPS)
      .map((o) => [o.top - hh - gap, o.bottom + hh + gap] as Interval);

  // 上排候选行（自顶向下，最多 4 行；行距 = 卡高 + 间距）
  const rowY: number[] = [];
  for (let r = 0; r < 4; r++) {
    const y = yMin + r * stepY;
    if (y > yMax + EPS) break;
    rowY.push(y);
  }
  const rowIvs = rowY.map((y) => subtractAll([[xMin, xMax] as Interval], holesForRow(y)));
  const capTop = rowIvs.map((ivs) => capacityOf(ivs, stepX));

  /**
   * 侧列：把「已使用的上排」整行当作障碍（保守，保证列不会顶到上排里）。
   * 注意膨胀量是**整张卡高 + 间距**：上排给的是中心 y，两张卡中心同 x 时
   * 中心距必须 ≥ card.h + gap 才不重叠（用 hh + gap 会漏 hh，实测会造成 1 对重叠）。
   */
  const colIvsFor = (x: number, rowsUsed: number): Interval[] => {
    const holes = holesForCol(x);
    for (let r = 0; r < rowsUsed; r++) holes.push([rowY[r] - card.h - gap, rowY[r] + card.h + gap]);
    return subtractAll([[yMin, yMax] as Interval], holes);
  };

  // 选择上排行数：够用就停，不够就多开一行（人数感知）
  let rowsUsed = 1;
  let bestTotal = -1;
  let chosen = { rowsUsed: 1, ivsL: [] as Interval[], ivsR: [] as Interval[], capL: 0, capR: 0 };
  for (let ru = 1; ru <= rowY.length; ru++) {
    const ivsL = colIvsFor(xMin, ru);
    const ivsR = colIvsFor(xMax, ru);
    const capL = capacityOf(ivsL, stepY);
    const capR = capacityOf(ivsR, stepY);
    const total = capTop.slice(0, ru).reduce((a, b) => a + b, 0) + capL + capR;
    if (total > bestTotal) {
      bestTotal = total;
      chosen = { rowsUsed: ru, ivsL, ivsR, capL, capR };
      rowsUsed = ru;
    }
    if (total >= inp.count) {
      chosen = { rowsUsed: ru, ivsL, ivsR, capL, capR };
      rowsUsed = ru;
      bestTotal = total;
      break;
    }
  }

  const k = Math.max(0, inp.count);
  const { capL, capR, ivsL, ivsR } = chosen;
  const perRow = new Array(rowsUsed).fill(0) as number[];

  /**
   * 分配：左右**对称**优先（真实牌桌的观感），侧位数 s = ⌊k/3⌋ 并受两列容量夹紧；
   * 上排承担 k − 2s，超过上排容量就增大 s；仍有余量再左右轮流补、最后回填上排后续行。
   */
  let s = Math.min(capL, capR, Math.floor(k / 3));
  // 上排第一行放不下「非侧位」的部分就往两列挪；但两列合计不得超过总人数
  // （`2*(s+1) <= k`）——否则当上排整行被中心带堵死（capTop[0]===0，竖屏常态）时，
  // 这个循环会把 s 顶过 k/2，`rest` 变负、`feasible` 被误判为 false（人少也被迫降档）。
  while (k - 2 * s > (capTop[0] ?? 0) && s < Math.min(capL, capR) && 2 * (s + 1) <= k) s++;
  let nL = s;
  let nR = s;
  perRow[0] = Math.max(0, Math.min(k - 2 * s, capTop[0] ?? 0));
  let rest = k - perRow[0] - nL - nR;
  while (rest > 0) {
    if (nL < capL && nL <= nR) {
      nL++;
      rest--;
      continue;
    }
    if (nR < capR) {
      nR++;
      rest--;
      continue;
    }
    let placed = false;
    for (let r = 0; r < rowsUsed; r++) {
      if (perRow[r] < (capTop[r] ?? 0)) {
        perRow[r]++;
        rest--;
        placed = true;
        break;
      }
    }
    if (!placed) break;
  }
  const feasible = rest === 0;
  if (rest > 0) perRow[0] += rest; // 兜底：压缩上排间距，绝不丢席位

  // ---- 生成坐标：先侧列，再上排（上排的圆角下压量要被真实的列位夹紧）----
  const shape = inp.shape ?? null;

  /** 侧列某个位置的 x：按圆角内收，位移被「该 y 带的可用 x 区间」夹紧。 */
  const colPointX = (y: number, side: -1 | 1): number => {
    const base = side < 0 ? xMin : xMax;
    if (!shape) return base;
    const need = Math.max(cornerLeft(y - hh, H, shape), cornerLeft(y + hh, H, shape));
    if (need <= 0) return base;
    const want = side < 0 ? need + hw : W - need - hw;
    const ivs = subtractAll([[xMin, xMax] as Interval], holesForRow(y));
    const own = ivs.find(([a, b]) => base >= a - EPS && base <= b + EPS);
    if (!own) return base;
    return side < 0 ? Math.min(want, own[1]) : Math.max(want, own[0]);
  };

  const leftYs = allocate(ivsL, nL, stepY);
  const rightYs = allocate(ivsR, nR, stepY);
  const leftPts = [...leftYs].reverse().map((y) => ({ x: colPointX(y, -1), y }));
  const rightPts = rightYs.map((y) => ({ x: colPointX(y, 1), y }));
  const colRects: Rect[] = [...leftPts, ...rightPts].map((p) => ({
    left: p.x - hw,
    right: p.x + hw,
    top: p.y - hh,
    bottom: p.y + hh,
  }));

  /** 上排某个位置的 y：按圆角下压，位移被「可用 y 区间 / 下一行 / 已放的列位」三重夹紧。 */
  const rowPointY = (x: number, r: number): number => {
    const base = rowY[r];
    if (!shape) return base;
    const need = Math.max(cornerTop(x - hw, W, shape), cornerTop(x + hw, W, shape));
    const want = need + hh; // 卡片上边 = need
    if (want <= base) return base;
    const ivs = subtractAll([[yMin, yMax] as Interval], holesForCol(x));
    const own = ivs.find(([a, b]) => base >= a - EPS && base <= b + EPS);
    let limit = own ? own[1] : base;
    if (r + 1 < rowsUsed && perRow[r + 1] > 0) limit = Math.min(limit, rowY[r + 1] - stepY);
    for (const cr of colRects) {
      if (cr.left < x + hw + gap - EPS && cr.right > x - hw - gap + EPS) {
        limit = Math.min(limit, cr.top - gap - hh);
      }
    }
    return Math.min(want, Math.max(base, limit));
  };

  const seats: Point[] = [];
  // 左列自下而上（顺时针从「我」的左手边开始）
  seats.push(...leftPts);
  // 上排自左向右
  for (let r = 0; r < rowsUsed; r++) {
    const xs = allocate(rowIvs[r], perRow[r], stepX);
    for (const x of xs) seats.push({ x, y: rowPointY(x, r) });
  }
  // 右列自上而下
  seats.push(...rightPts);

  return {
    density: sp.key,
    card,
    me: mePos,
    seats: seats.slice(0, k),
    feasible: feasible && seats.length >= k,
    diag: {
      topRows: rowsUsed,
      capTop: capTop.slice(0, rowsUsed),
      capLeft: capL,
      capRight: capR,
      used: { top: perRow, left: nL, right: nR },
      total: bestTotal,
    },
  };
}

/**
 * M1 · Round25 座位椭圆规格（design §4.1.1，归一化 [0,1]）。座位落点是「人数 → 角度」的
 * 确定性查表 → 椭圆公式 → 贴边夹紧，**不是运行时装箱结果**（G1：桌心留白由构造保证，不靠避让）。
 */
export const SEAT_ELLIPSE = {
  /** 对手环椭圆中心（略高于视觉中线，把桌心让给公共牌）。 */
  cx: 0.5,
  cy: 0.47,
  /** 水平 / 垂直半径。 */
  rx: 0.4,
  ry: 0.41,
} as const;

/**
 * M1 · Round25 逐人数对手角度序列（含 Hero 的总人数 → 对手角度，deg）。
 * `a` 从底部中央起算，`a=180°` = 顶部中央；对称分布于顶部两侧。底部 ±40° 扇区留给 Hero。
 *
 * 采用范围（design §4.1.2 定稿标准桌 2/3/6/8）：本实现对 **2/3/8** 落地确定性椭圆——这三档在
 * 全设备矩阵（358×640 / 343×500 / 328×470 及 390 竖屏）下，椭圆落点既通过 `validateRing` 硬约束，
 * 又通过 M2 席位簇模板的归属护栏（`assertAttribution` κ≤0.6，见 cluster-layout 全矩阵）。
 * **6 档暂不启用椭圆**：其 compact pod（72×88）在高视口（h≈640）下椭圆均布会把左列 pod 与其行动 pill
 * 拉到 κ≈0.78 的归属歧义（> 0.6 红线），会迫使 M2 簇模板回退阶段1 求解器（违反「阶段2 不回退」红线）。
 * 故 6 档回退到已验证的装箱分布（`planRingWith`，本身即椭圆状均布且真机零遮挡），返回 null。
 */
export function seatAngles(total: number): number[] | null {
  switch (total) {
    case 2:
      return [180];
    case 3:
      return [145, 215];
    case 8:
      return [180, 145, 215, 105, 255, 62, 298];
    default:
      return null;
  }
}

/** 把 §4.1 归一化角度换算成 felt 内容盒 px 坐标（含 §4.1.1 贴边夹紧）。 */
export function ellipseSeatPoints(total: number, box: Size, hw: number, hh: number, edge: number): Point[] | null {
  const angles = seatAngles(total);
  if (!angles) return null;
  const { cx, cy, rx, ry } = SEAT_ELLIPSE;
  const xMin = edge + hw;
  const xMax = box.w - edge - hw;
  const yMin = edge + hh;
  const yMax = box.h - edge - hh;
  const clamp = (v: number, lo: number, hi: number) => (lo > hi ? (lo + hi) / 2 : Math.min(Math.max(v, lo), hi));
  const pts = angles.map((a) => {
    const rad = (a * Math.PI) / 180;
    const nx = cx + rx * Math.sin(rad);
    const ny = cy + ry * Math.cos(rad);
    return { x: clamp(nx * box.w, xMin, xMax), y: clamp(ny * box.h, yMin, yMax) };
  });
  // 顺时针稳定排序（左列自下而上 → 顶部 → 右列自上而下），与既有 RingPlan.seats 契约一致。
  const mid = box.w / 2;
  const left = pts.filter((p) => p.x < mid - EPS).sort((a, b) => b.y - a.y);
  const center = pts.filter((p) => Math.abs(p.x - mid) <= EPS).sort((a, b) => a.x - b.x);
  const right = pts.filter((p) => p.x > mid + EPS).sort((a, b) => a.y - b.y);
  return [...left, ...center, ...right];
}

/**
 * M1 · Round25 确定性椭圆环布：为标准桌（2/3/6/8 人）在指定档位上按 §4.1 规格坐标落点。
 * Hero 落点与 `planRingWith` 一致（底部居中）。校验交由调用方 `validateRing`，不满足则回退装箱。
 */
function ellipseRingWith(inp: RingInput, sp: DensitySpec): RingPlan | null {
  const edge = inp.edge ?? 8;
  const H = inp.box.h;
  const card = sp.ring;
  const seats = ellipseSeatPoints(inp.count + 1, inp.box, card.w / 2, card.h / 2, edge);
  if (!seats) return null;
  const meH = Math.min(inp.me.h, Math.max(0, H - 2 * edge));
  const meY = H - edge - meH / 2;
  return {
    density: sp.key,
    card,
    me: { x: inp.box.w / 2, y: meY },
    seats,
    feasible: true,
    diag: { topRows: 0, capTop: [], capLeft: 0, capRight: 0, used: { top: [], left: 0, right: 0 }, total: seats.length },
  };
}

/** 环形（宽屏 oval / 竖屏 stack 通用）布局：按档位阶梯自动降档，返回第一个可行方案。 */
export function planRing(inp: RingInput): RingPlan {
  const table = inp.densities ?? DENSITIES;
  if (inp.density) return planRingWith(inp, specFrom(table, inp.density));
  /**
   * M1 · Round25：竖屏标准桌（2/3/6/8 人）优先走「确定性椭圆均布」（design §4.1）。
   * 仅对竖屏档表 `DENSITIES_PORTRAIT`（含其**后缀切片**，Table.tsx 按人数 `slice(startIdx)` 喂档）生效
   * —— 用元素引用成员判定（`slice` 保留同一档位对象引用），宽屏 `DENSITIES` 零回归；且**只有当某档椭圆
   * 落点实测通过硬约束校验（不越界 / 不重叠 / 不侵入 Hero / 中心带）才采用**；任一档都不满足则回退装箱，
   * 保证极窄机型仍「宁可缩小也不重叠」。
   */
  if (table.length > 0 && DENSITIES_PORTRAIT.includes(table[0]) && seatAngles(inp.count + 1)) {
    for (const sp of table) {
      const plan = ellipseRingWith(inp, sp);
      if (plan && validateRing(plan, inp).ok) return plan;
    }
  }
  let last: RingPlan | null = null;
  for (const sp of table) {
    const plan = planRingWith(inp, sp);
    last = plan;
    if (plan.feasible && validateRing(plan, inp).ok) return plan;
  }
  return last!;
}

export interface GridInput {
  /** 对手区可用内容盒。 */
  box: Size;
  count: number;
  /** 最大列数，缺省 4。 */
  maxColumns?: number;
}

export interface GridPlan {
  tier: SeatDensity;
  columns: number;
  rows: number;
  rowH: number;
  gap: number;
  minColW: number;
  /** 是否「不滚动就放得下」。 */
  feasible: boolean;
}

/** micro 卡片的内容硬下限：上下 padding 1+1、上下边框 1+1、单行文字行高 14。 */
export const MIN_ROW_H = 18;

/**
 * 终极档位：micro + 列数拉满 + 行高压到可用高度。
 * 只有当所有标称档位都放不下时才用它 —— 它保证「占位最小」，因此不可能比任何
 * 标称方案更容易越界；行高压缩下限是 `MIN_ROW_H`，低于此值 `feasible=false`
 * （几何上确实无解，此时也仍然返回占位最小方案，不做放大）。
 */
function squeezeGrid(box: Size, k: number, maxColumns: number): GridPlan {
  const { minColW, gap } = spec('micro').grid;
  let cols = Math.min(maxColumns, k);
  while (cols > 1 && (box.w - (cols - 1) * gap) / cols < minColW - EPS) cols--;
  const rows = Math.ceil(k / cols);
  const avail = box.h - (rows - 1) * gap;
  const rowH = Math.max(MIN_ROW_H, Math.floor(avail / rows));
  const need = rows * rowH + (rows - 1) * gap;
  return { tier: 'micro', columns: cols, rows, rowH, gap, minColW, feasible: need <= box.h + EPS };
}

/**
 * 网格（窄屏）布局：在「列数 × 档位」空间里找**不需要滚动**的最松方案。
 * 判据是硬几何：行数 × 行高 + 行间隙 ≤ 可用高度，且列宽 ≥ 档位最小列宽。
 * 全部档位都放不下时退到 `squeezeGrid`（占位最小），**绝不**退回占位更大的档位。
 */
export function planGrid({ box, count, maxColumns = 4 }: GridInput): GridPlan {
  const k = Math.max(1, count);
  for (const sp of DENSITIES) {
    if (sp.key === 'compact') continue; // 网格下与 full 同高，无收益
    const { minColW, rowH, gap } = sp.grid;
    /** 同一档位里选「行数最少」的列数（行数相同再取列数最少 = 单卡最宽）。 */
    let best: GridPlan | null = null;
    for (let cols = 1; cols <= Math.min(maxColumns, k); cols++) {
      const colW = (box.w - (cols - 1) * gap) / cols;
      if (colW < minColW - EPS) continue;
      const rows = Math.ceil(k / cols);
      const need = rows * rowH + (rows - 1) * gap;
      if (need > box.h + EPS) continue;
      if (!best || rows < best.rows) best = { tier: sp.key, columns: cols, rows, rowH, gap, minColW, feasible: true };
    }
    if (best) return best;
  }
  return squeezeGrid(box, k, maxColumns);
}

export interface RingViolationCheck {
  ok: boolean;
  violations: string[];
}

/** 环形方案的硬约束校验：越界 / 重叠 / 侵入我的排他区 / 侵入中心带 / 侵入浮层。 */
export function validateRing(plan: RingPlan, inp: RingInput): RingViolationCheck {
  const gap = inp.gap ?? 8;
  const edge = inp.edge ?? 8;
  const W = inp.box.w;
  const H = inp.box.h;
  const v: string[] = [];
  const rects = plan.seats.map((p) => ({
    left: p.x - plan.card.w / 2,
    right: p.x + plan.card.w / 2,
    top: p.y - plan.card.h / 2,
    bottom: p.y + plan.card.h / 2,
  }));

  if (plan.seats.length !== inp.count) v.push(`席位数 ${plan.seats.length} ≠ 对手数 ${inp.count}`);

  rects.forEach((r, i) => {
    if (r.left < -EPS || r.top < -EPS || r.right > W + EPS || r.bottom > H + EPS) {
      v.push(`#${i} 越出内容盒 (${Math.round(r.left)},${Math.round(r.top)},${Math.round(r.right)},${Math.round(r.bottom)})`);
    }
  });

  for (let i = 0; i < rects.length; i++) {
    for (let j = i + 1; j < rects.length; j++) {
      if (inter(rects[i], rects[j])) v.push(`#${i}↔#${j} 两两重叠`);
    }
  }

  const meH = Math.min(inp.me.h, Math.max(0, H - 2 * edge));
  const meRect: Rect = {
    left: plan.me.x - inp.me.w / 2 - gap,
    right: plan.me.x + inp.me.w / 2 + gap,
    top: plan.me.y - meH / 2 - gap,
    bottom: H,
  };
  rects.forEach((r, i) => {
    if (inter(r, meRect)) v.push(`#${i} 侵入「我」的排他区`);
  });

  if (inp.band) {
    const band: Rect = {
      left: W / 2 - inp.band.halfWidth - gap,
      right: W / 2 + inp.band.halfWidth + gap,
      top: inp.band.top - gap,
      bottom: inp.band.bottom + gap,
    };
    rects.forEach((r, i) => {
      if (inter(r, band)) v.push(`#${i} 侵入受保护中心带`);
    });
  }

  (inp.blockers ?? []).forEach((b, bi) => {
    const grown: Rect = { left: b.left - gap, top: b.top - gap, right: b.right + gap, bottom: b.bottom + gap };
    rects.forEach((r, i) => {
      if (inter(r, grown)) v.push(`#${i} 侵入浮层#${bi}`);
    });
  });

  return { ok: v.length === 0, violations: v };
}


// ===========================================================================
// 阶段2（M2）· 席位簇（SeatCluster）模板根治（设计方案 §4/§5/§6，落地清单 §9.2）
// ===========================================================================
//
// ## 为什么要有簇模板
//
// 阶段1（`overlayLayout.solveOverlayLayout`）用「统一 occupancy + 运行时可行域扫描 + 声明式降级」
// 把全部卫星纳入同一坐标系并保证零重叠 —— 这是**稳定回退基座**。阶段2 在同一快照/不变量骨架上，
// 把「运行时求解」替换为「**模板构造**」：每个席位的全部元素（pod 最外 → 牌背 → chip → pill，
// badge/D 占 pod 固定角槽）被绑成一个「簇」，槽位偏移由密度档 + pod→底池径向轴**给死为确定量**
// （§4.2），天然不叠、无需运行时碰撞扫描（S2-1/S2-3）。
//
// ## 单一数据源（避免两套尺寸表漂移）
//
// 卫星尺寸表 `SAT_SIZES` / 间隙表 `GAPS` / 牌背旋转常量 `CARD_ROT` 等**全部复用 M1 的
// `overlayLayout.ts`**（本文件仅 re-export，供 CSS/模板/测试单一真源引用），不复制第二份。
// 「固定障碍装配」`initFixed` 与「占位/白名单判定口径」`Occupancy.freeFor` / `assertNoOverlap`
// 同样复用 M1，**阶段2 不引入第二套判定**（设计 §4.3）。
//
// ## 零重叠保证（准入门槛：M1 全矩阵不回退）
//
// `clusterPlanToSnapshot` 把簇模板落位时，每个被放置的卫星都过 M1 的 `Occupancy.freeFor`
// （与 `assertNoOverlap` 同源同白名单）；短射线（`R_free < L`）按 §5.3 阶梯压缩（牌背 rot→0 /
// chip 缩档 / chip+pill 合并复合 pill / 省略），压缩后仍放不下的**瞬态卫星**（chip/pill/牌背）
// 直接省略（信息收敛而非叠压）。故 `validateClusters` 恒 `ok` —— 与 M1 求解器同样「构造即正确」。

import {
  SAT_SIZES,
  GAPS,
  Z,
  initFixed,
  unit,
  perp,
  proj,
  obbAt,
  rotOf,
  radialFree,
  measureAttribution,
  type OverlayInput,
  type OBB,
  type LayoutElement,
  type LayoutSnapshot,
  type SatelliteSizeTable,
  type GapTable,
} from './overlayLayout';
import {
  assertNoOverlap,
  assertAttribution,
  ATTRIBUTION_POLICY_DEFAULT,
  LAYOUT_POLICY_DEFAULT,
  type LayoutPolicy,
  type AttributionPolicy,
} from './layoutInvariant';

/** 卫星尺寸/间隙表单一真源（复用 M1 `overlayLayout`，供 seatLayout 消费方引用，不复制第二份）。 */
export { SAT_SIZES, GAPS };
export type { SatelliteSizeTable, GapTable };

/** 簇内单个模板槽位（已随密度档取具体尺寸，设计 §4.1）。 */
export interface ClusterSlot {
  kind: 'cardback' | 'chip' | 'pill' | 'badge';
  role?: 'SB' | 'BB' | 'D';
  /** 沿径向轴偏移（自 pod 中心，px；badge 为其角槽径向分量）。 */
  d: number;
  /** 切向偏移（自径向轴，px，沿 tangent 正向；径向槽为 0，badge 角槽非 0）。 */
  t: number;
  /** 本地盒（cardback rot=±6°/0，其余 0）。 */
  hw: number;
  hh: number;
  rot: number;
}

/** 一个席位相关的全部元素被绑成一个「簇」（设计 §4.1）。 */
export interface SeatCluster {
  seat: number;
  /** pod 中心（= ring.seats[idx]）。 */
  pod: Point;
  /** 径向单位向量 pod→pot。 */
  axis: { ux: number; uy: number };
  tangent: { tx: number; ty: number };
  innerSide: 'left' | 'right' | 'bottom';
  /** 簇张角一半（rad），计入 planRing 容量（§5）。 */
  halfAngle: number;
  /** 簇切向预留半宽（px），planRing 步长膨胀用（§5.2）。 */
  reserveTangential: number;
  /** 固定模板槽位（随密度档 + 运行态裁剪）。 */
  slots: ClusterSlot[];
}

/** planRing 在 pod 求解基础上额外输出的簇计划（设计 §4.1）。 */
export interface ClusterPlan extends RingPlan {
  clusters: SeatCluster[];
  pot: Point;
  /** 当前密度档卫星尺寸（§6）。 */
  sizes: SatelliteSizeTable;
}

/**
 * 簇步长膨胀（设计 §5.2 · S2-2）：把「恒在卫星」的切向预留计入 planRing 步长，保证相邻簇的
 * 恒在元素（pod + 牌背）跨席位零重叠。因 `reserveTangential = max(pod 切向半径, 牌背切向半extent)`
 * 而 pod 恒 ≥ 牌背（§6 表），故 `2·reserveTangential ≡ card 边长` —— **实质等价于现状 pod 间距**
 * （膨胀量 0），planRing 容量判据不变、不误降档（M1 全矩阵不回退）。仅当未来牌背切向超过 pod 时
 * 才真正膨胀，届时自动多留净空。
 */
export function clusterStep(
  card: Size,
  reserveTangentialTopArc: number,
  reserveTangentialSideCol: number,
  gap: number,
): { stepX: number; stepY: number } {
  return {
    stepX: Math.max(card.w, 2 * reserveTangentialTopArc) + gap,
    stepY: Math.max(card.h, 2 * reserveTangentialSideCol) + gap,
  };
}

/**
 * planRing 扩展输出簇计划（S2-1）：在既有 pod 求解结果（`inp.ring`）之上，为每个对手席位计算
 * 径向轴（pod 中心→底池方向）、切向、簇张角/切向预留，与固定模板槽位（pod 最外→牌背→chip→pill，
 * badge/D 占 pod 固定角槽）。槽位偏移为**密度档给死的确定量**（沿 §3.2.1 的 `proj()` 链在真实方向
 * 上连续插值 = 设计 §4.2 逐档槽位表的取值），天然不叠、无需运行时碰撞扫描。
 */
export function planClusters(inp: OverlayInput): ClusterPlan {
  const density = inp.ring.density;
  const sizes = inp.sizes ?? SAT_SIZES[density];
  const gaps = inp.gaps ?? GAPS[density];
  const P = inp.pot;
  const clusters: SeatCluster[] = [];

  for (const s of inp.seats) {
    const u = unit(P.x - s.pod.cx, P.y - s.pod.cy);
    const t = perp(u);
    const podEdge = proj(s.pod.hw, s.pod.hh, u); // pod 沿 u 支撑半径
    const podTan = proj(s.pod.hw, s.pod.hh, t); // pod 切向半径
    const rot = rotOf(s.innerSide);
    const rotHalf = sizes.cardbackRotHalf;
    const slots: ClusterSlot[] = [];

    // badge/D 角槽：贴 pod 下角（径向略沉 + 切向沉桌心；D 与 SB/BB 分列 pod 两角，t 反向）。
    const dRad = podEdge * 0.5;
    if (s.blind === 'SB' || s.blind === 'BB') {
      const bhw = sizes.badge.w / 2;
      const bhh = sizes.badge.h / 2;
      slots.push({ kind: 'badge', role: s.blind, d: dRad, t: podTan + Math.max(bhw, bhh) + 2, hw: bhw, hh: bhh, rot: 0 });
    }
    if (s.isDealer) {
      const dhw = sizes.dealer.w / 2;
      const dhh = sizes.dealer.h / 2;
      slots.push({ kind: 'badge', role: 'D', d: dRad, t: -(podTan + Math.max(dhw, dhh) + 2), hw: dhw, hh: dhh, rot: 0 });
    }

    // 径向链（自 pod 中心，用 proj 连续插值 = §3.2.1 公式的密度档取值）：cardback → chip → pill。
    let reservedR = podEdge;
    if (s.inHand && (!s.folded || s.revealAll)) {
      const dCard = podEdge + gaps.g0 + proj(rotHalf.hw, rotHalf.hh, u);
      slots.push({ kind: 'cardback', d: dCard, t: 0, hw: sizes.cardback.w / 2, hh: sizes.cardback.h / 2, rot });
      reservedR = dCard + proj(rotHalf.hw, rotHalf.hh, u);
    }
    if (s.streetContributed > 0) {
      const dChip = reservedR + gaps.g1 + proj(sizes.chip.w / 2, sizes.chip.h / 2, u);
      slots.push({ kind: 'chip', d: dChip, t: 0, hw: sizes.chip.w / 2, hh: sizes.chip.h / 2, rot: 0 });
      reservedR = dChip + proj(sizes.chip.w / 2, sizes.chip.h / 2, u);
    }
    if (s.actionKind) {
      const dPill = reservedR + gaps.g2 + proj(sizes.pill.w / 2, sizes.pill.h / 2, u);
      slots.push({ kind: 'pill', d: dPill, t: 0, hw: sizes.pill.w / 2, hh: sizes.pill.h / 2, rot: 0 });
    }

    const reserveTangential = Math.max(podTan, proj(rotHalf.hw, rotHalf.hh, t));
    const dist = Math.hypot(P.x - s.pod.cx, P.y - s.pod.cy) || 1;
    const halfAngle = Math.atan2(reserveTangential, dist);
    clusters.push({
      seat: s.seat,
      pod: { x: s.pod.cx, y: s.pod.cy },
      axis: { ux: u.x, uy: u.y },
      tangent: { tx: t.x, ty: t.y },
      innerSide: s.innerSide,
      halfAngle,
      reserveTangential,
      slots,
    });
  }

  return { ...inp.ring, clusters, pot: P, sizes };
}

/**
 * 簇模板 → LayoutSnapshot（S2-3 纯映射 · 设计 §4.1/§5.3）。
 *
 * 复用 M1 的固定障碍装配（`initFixed`）与占位/白名单判定（`Occupancy.freeFor`，与 `assertNoOverlap`
 * 同口径）。逐簇按「席位优先级（近底池短射线先占）× 元素优先级（badge/D > cardback > chip > pill）」
 * 把槽位落到 `pod 中心 + u·d + t·t`：命中理想槽即放（长射线常态 = **纯映射，无扫描**）；否则按 §5.2
 * 切向错位（±k·tStep，K 档）微调；仍无解按 §5.3 阶梯压缩（牌背 rot→0 / chip 缩档 / chip+pill 合并
 * 复合 pill / 省略瞬态卫星）。**绝不叠压**：任何被放置元素都过 `freeFor`，放不下的瞬态卫星直接省略。
 */
export function clusterPlanToSnapshot(plan: ClusterPlan, inp: OverlayInput): LayoutSnapshot {
  const { occ, elements, degradations, density, gaps, P, box } = initFixed(inp);
  const sizes = plan.sizes;
  const pol = ATTRIBUTION_POLICY_DEFAULT;

  // 全席 pod OBB（κ / 跨主人判定用）；淘汰席（eliminated）既不作主人也不作 d_nearest_other 候选（AC-A8）。
  const seatPods = new Map<number, OBB>();
  for (const s of inp.seats) {
    if (s.eliminated) continue;
    seatPods.set(s.seat, obbAt(s.pod.cx, s.pod.cy, s.pod.hw, s.pod.hh, 0));
  }

  // 席位优先级：近底池短射线先占（空间紧张者先占，与阶段1 同口径）。
  const ordered = [...inp.seats].filter((s) => !s.eliminated).sort((a, b) => radialFree(a, P) - radialFree(b, P));

  /** 两 rot≈0 卫星沿中心连线的外缘间距（与 assertAttribution 跨主人口径一致）。 */
  const edgeGapBetween = (a: OBB, b: OBB): number => {
    const cd = Math.hypot(a.cx - b.cx, a.cy - b.cy);
    if (cd < 1e-6) return 0;
    const dir = { x: (b.cx - a.cx) / cd, y: (b.cy - a.cy) / cd };
    return cd - proj(a.hw, a.hh, dir) - proj(b.hw, b.hh, dir);
  };

  /**
   * 候选是否满足「归属硬约束 + 跨主人语义间距」（与 `assertAttribution` **完全同口径**）：
   * `edgeGapOwn ≤ gapMax`；`κ = d_own/d_nearest_other ≤ κ_hard(0.6)`；跨主人（不同席 pill/chip/badge）
   * 中心距 ≥ 24、外缘 ≥ 12。任一不满足即拒绝该候选（→ 换承载模式 / 收敛隐藏），**无放宽 κ 的路径**。
   * 归属由此**构造**保证，不由优化保证。
   */
  const attributionOk = (cand: LayoutElement, ownerSeat: number, gapMax: number): boolean => {
    const owner = seatPods.get(ownerSeat);
    if (!owner) return false;
    const others: OBB[] = [];
    for (const [seat, obb] of seatPods) if (seat !== ownerSeat) others.push(obb);
    const m = measureAttribution(cand.obb, owner, others);
    if (m.edgeGapOwn > gapMax + pol.eps) return false;
    if (Number.isFinite(m.dNearestOther) && m.kappa > pol.kappaHard) return false;
    const crossKind = cand.kind === 'chip' || cand.kind === 'pill' || cand.kind === 'badge';
    if (crossKind) {
      for (const e of occ.placed) {
        if (e.seat === null || e.seat === ownerSeat) continue;
        if (!(e.kind === 'chip' || e.kind === 'pill' || e.kind === 'badge')) continue;
        const cd = Math.hypot(cand.obb.cx - e.obb.cx, cand.obb.cy - e.obb.cy);
        if (cd < pol.centerDistMin - pol.eps) return false;
        if (edgeGapBetween(cand.obb, e.obb) < pol.sMinCrossOwner - pol.eps) return false;
      }
    }
    return true;
  };

  type Mode = 'radial' | 'tangential' | 'inPod';
  interface Cand {
    cx: number;
    cy: number;
    hw: number;
    hh: number;
    rot: number;
    mode: Mode;
  }

  /** 按承载模式优先级尝试落位：命中首个「freeFor + attributionOk」的候选即入 occ 并返回。 */
  const commit = (
    cands: Cand[],
    meta: Omit<LayoutElement, 'obb'>,
    gapMax: number,
    ownerSeat: number,
    slot?: 'P3' | 'P4' | 'P5',
  ): LayoutElement | null => {
    for (const c of cands) {
      const degraded = c.mode === 'inPod' ? 'inPod' : c.mode === 'tangential' ? 'tangential' : meta.degraded;
      const el: LayoutElement = {
        ...meta,
        obb: obbAt(c.cx, c.cy, c.hw, c.hh, c.rot),
        degraded,
        podSlot: c.mode === 'inPod' ? slot : undefined,
      };
      if (!occ.freeFor(el)) continue;
      if (!attributionOk(el, ownerSeat, gapMax)) continue;
      occ.add(el);
      elements.push(el);
      return el;
    }
    return null;
  };

  for (const s of ordered) {
    const S: Point = { x: s.pod.cx, y: s.pod.cy };
    const u = unit(P.x - S.x, P.y - S.y);
    const t = perp(u);
    const podEdgeU = proj(s.pod.hw, s.pod.hh, u);
    const podTanT = proj(s.pod.hw, s.pod.hh, t);
    // 背离最近邻座的切向正负号（tangential 优先朝净空大的一侧）。
    let awaySign = 1;
    {
      let best = Infinity;
      for (const o of ordered) {
        if (o.seat === s.seat) continue;
        const dd = Math.hypot(o.pod.cx - S.x, o.pod.cy - S.y);
        if (dd < best) {
          best = dd;
          awaySign = (o.pod.cx - S.x) * t.x + (o.pod.cy - S.y) * t.y >= 0 ? -1 : 1;
        }
      }
    }
    // pod 上缘标识带中心（相对高度 0.16，落在 0~0.35h 内，避开 podTextRect 0.35~0.9h 文本区）。
    const inPodCy = s.pod.cy - s.pod.hh + 0.16 * 2 * s.pod.hh;
    /**
     * Round21 §4.2/§4.3.4：P3 内嵌动作条的 cy 单独下压到「上缘标识带底部、文本区上沿之上 1px」。
     *
     * 为什么不复用 inPodCy(0.16)：S3（68×60）侧列 pod 常态是**上下相邻**（中心距 = 60+gap8 = 68），
     * 若动作条仍居 0.16 高处（dOwn=20.4）则朝上方邻座偏移 → 到邻座中心仅 47.6px、κ=0.425 > 设计
     * inPod 上限 0.35。把动作条压到文本区（0.35h）上沿正上方，`dOwn` 由 20.4 收到 ≈17，同时**远离**
     * 上邻座，κ 落到 ≈0.33 ≤ 0.35（AC-A：inPod 动作标签 κ 达设计目标），且仍在 0~0.35h 带内不压文本。
     */
    const inPodActionCy = s.pod.cy - s.pod.hh + Math.max(0.16 * 2 * s.pod.hh, 0.35 * 2 * s.pod.hh - sizes.actionbarInPod.h / 2 - 1);
    const csx = Math.sign(u.x) || 1; // 桌心侧水平方向（u 的水平分量朝底池）

    const radialCand = (sz: Size, rot: number): Cand => {
      const hw = sz.w / 2;
      const hh = sz.h / 2;
      const dObb = podEdgeU + gaps.g1 + proj(hw, hh, u);
      /**
       * R21-M5 · BUG-03 / BUG-09（径向卫星贴 pod 太松）：径向距离原用 `proj()`（OBB 沿 u 的支撑半径），
       * 当 pod→底池射线 u 斜向、且 pod / 卫星较宽时，支撑半径 > 该盒在**主导轴**上的真实半高/半宽，
       * 于是与 pod 的 **AABB 外缘距**（QA/AC 口径：axis-aligned 逐轴净空）被半宽差放大——折叠席仅一枚
       * 「弃牌」pill 直贴 pod 实测 edgeGap≈14.8>12（BUG-03），大 pod（N≤6）牌背中心→头像 83.5>56（BUG-09）。
       * 修复：再算一条 **AABB 贴靠距离**——令主导轴外缘距 = 设计间隙 g1，取两轴所需 along-u 距离较小者
       * （= 最紧贴靠、主导轴已正分离 → 与 pod 不叠），与 OBB 距离取 min（只更近、绝不更远；u 近轴向时二者相等）。
       * 收紧后**只是候选**，仍要过 `commit` 的 `freeFor`（同 assertNoOverlap 白名单）+ 归属校验：
       * 若与牌背 / 相邻卫星 / 文本区相交即被否决、回退切向或内嵌，零重叠不变量恒成立。
       * 旋转盒（牌背 ±6°）按其 AABB 半径 (|hw·cos|+|hh·sin|, |hw·sin|+|hh·cos|) 计算，贴靠口径精确。
       */
      const ca = Math.abs(Math.cos(rot));
      const sa = Math.abs(Math.sin(rot));
      const ehw = hw * ca + hh * sa;
      const ehh = hw * sa + hh * ca;
      const dV = Math.abs(u.y) > 1e-3 ? (s.pod.hh + ehh + gaps.g1) / Math.abs(u.y) : Infinity;
      const dH = Math.abs(u.x) > 1e-3 ? (s.pod.hw + ehw + gaps.g1) / Math.abs(u.x) : Infinity;
      const d = Math.min(dObb, dV, dH);
      return { cx: S.x + u.x * d, cy: S.y + u.y * d, hw, hh, rot, mode: 'radial' };
    };
    const tangCand = (sz: Size, sign: number): Cand => {
      const d = podTanT + gaps.g1 + proj(sz.w / 2, sz.h / 2, t);
      return { cx: S.x + t.x * sign * d, cy: S.y + t.y * sign * d, hw: sz.w / 2, hh: sz.h / 2, rot: 0, mode: 'tangential' };
    };
    const inPodCand = (sz: Size, dx: number, cy: number = inPodCy): Cand => ({
      cx: s.pod.cx + dx,
      cy,
      hw: sz.w / 2,
      hh: sz.h / 2,
      rot: 0,
      mode: 'inPod',
    });

    // ---- badges（SB/BB/D）：pod 上缘角槽，与 .seat__made（pod 底部）结构性异侧（AC-B3-③）----
    // 桌心侧上角优先；κ/间距不达标退外缘上角 / 外缘中部（-u，κ 最优），仍不行则隐藏（绝不歧义归属）。
    const placeBadge = (role: 'SB' | 'BB' | 'D', preferOuterFirst: boolean) => {
      const bw = role === 'D' ? sizes.dealer.w : sizes.badge.w;
      const bh = role === 'D' ? sizes.dealer.h : sizes.badge.h;
      const bhw = bw / 2;
      const bhh = bh / 2;
      const topY = s.pod.cy - s.pod.hh - bhh + 2; // 2px 压入 pod 上缘（pod↔badge 白名单 ≤18%/3px）
      const csCorner: Cand = { cx: s.pod.cx + csx * (s.pod.hw - bhw), cy: topY, hw: bhw, hh: bhh, rot: 0, mode: 'radial' };
      const outCorner: Cand = { cx: s.pod.cx - csx * (s.pod.hw - bhw), cy: topY, hw: bhw, hh: bhh, rot: 0, mode: 'radial' };
      const outMid: Cand = (() => {
        const d = podEdgeU + gaps.g0 + proj(bhw, bhh, u);
        return { cx: S.x - u.x * d, cy: S.y - u.y * d, hw: bhw, hh: bhh, rot: 0, mode: 'radial' };
      })();
      const cands = preferOuterFirst ? [outCorner, csCorner, outMid] : [csCorner, outCorner, outMid];
      const id = role === 'D' ? 'dealer' : `seat${s.seat}.badge.${role}`;
      const el = commit(cands, { id, kind: 'badge', seat: s.seat, role, z: Z.seatBadge }, pol.edgeGapMax.badge, s.seat);
      if (!el) degradations.push(`${id}:hidden`);
    };
    if (s.blind === 'SB' || s.blind === 'BB') placeBadge(s.blind, false);
    if (s.isDealer) placeBadge('D', true);

    // ---- cardback（在手席位；Round25 C2：全揭示态下已弃牌者也产出，摊牌 face-up 走统一锚点）：radial(±6) → rot0 → inPod 小徽(P4) → 隐藏 ----
    if (s.inHand && (!s.folded || s.revealAll)) {
      const rot = rotOf(s.innerSide);
      const cb = sizes.cardback;
      const mini = sizes.cardbackMini;
      const inPodCb = inPodCand(mini, -csx * Math.max(0, s.pod.hw - mini.w / 2 - 1));
      /**
       * R21-M5 · BUG-09（AC-D3「手牌贴脸」：牌背中心→头像中心 ≤56）：头像固定在 pod 顶部。
       * 顶弧席（innerSide='bottom'，u 正对底池朝**下**）若先走径向外扩，牌背会落到 pod 底、与顶部头像
       * 隔了整个 pod 高——大 pod（N≤6）实测牌背→头像 83.5>56。侧列席（u 带横向分量）径向本就落在头像
       * 肩侧（实测≈24 ✓），维持原序。故仅顶弧席**优先内嵌贴脸**：mini 牌背落 pod 上缘标识带（0.16h，
       * 避开 0.35~0.9h 文本区，同簇 inPod 白名单允许压自身 pod 上缘），紧邻头像；放不下再退径向 → rot0。
       */
      const cands: Cand[] = s.innerSide === 'bottom' ? [inPodCb, radialCand(cb, rot)] : [radialCand(cb, rot)];
      if (s.innerSide !== 'bottom') {
        if (rot !== 0) cands.push(radialCand(cb, 0));
        cands.push(inPodCb);
      }
      const meta: Omit<LayoutElement, 'obb'> = { id: `seat${s.seat}.cardback`, kind: 'cardback', seat: s.seat, z: Z.seat };
      const el = commit(cands, meta, pol.edgeGapMax.own, s.seat, 'P4');
      if (el) {
        if (el.degraded === 'inPod') degradations.push(`seat${s.seat}.cardback:inPod`);
        else if (el.obb.rot === 0 && rot !== 0) degradations.push(`seat${s.seat}.cardback:rot0`);
      } else {
        degradations.push(`seat${s.seat}.cardback:hidden`);
      }
    }

    // ---- 动作/筹码合并为单一标签（归属唯一、卫星数最小 §C1）：radial → tangential → inPod(P3) → 隐藏 ----
    const hasChip = s.streetContributed > 0;
    const hasAct = !!s.actionKind;
    if (hasChip || hasAct) {
      const merged = hasChip && hasAct;
      const kind: LayoutElement['kind'] = hasAct ? 'pill' : 'chip';
      const extSize = merged ? sizes.merged : hasAct ? sizes.pill : sizes.chip;
      const inSize = sizes.actionbarInPod;
      const cands: Cand[] = [
        radialCand(extSize, 0),
        tangCand(extSize, awaySign),
        tangCand(extSize, -awaySign),
        inPodCand(inSize, 0, inPodActionCy),
      ];
      const id = `seat${s.seat}.${kind}`;
      const meta: Omit<LayoutElement, 'obb'> = { id, kind, seat: s.seat, z: Z.bubble, degraded: merged ? 'merge' : undefined };
      const el = commit(cands, meta, pol.edgeGapMax.own, s.seat, 'P3');
      if (el) {
        if (merged) degradations.push(`seat${s.seat}.chip+pill:merge`);
        if (el.degraded === 'inPod') degradations.push(`${id}:inPod`);
        else if (el.degraded === 'tangential') degradations.push(`${id}:tangential`);
      } else if (hasAct) {
        /*
         * Round25 · M2.1 / C3（round25-pill-must-visible）：**行动 pill 是硬可见约束，禁止合规隐藏**
         * （废除根因问题5 的 `pill:hidden` violation-free 解）。穷尽 radial/tangential/inPod 常规解仍
         * 放不下时，**强制**落在头像内侧 inPod 槽（同主人、白名单内 → 不侵犯邻座 → assertNoOverlap 不破），
         * 保证真机 visFrac>0。记 `pill:forced`（可诊断），绝不 `pill:hidden`。
         */
        const f = inPodCand(inSize, 0, inPodActionCy);
        const forced: LayoutElement = {
          id, kind: 'pill', seat: s.seat, z: Z.bubble, degraded: 'inPod', podSlot: 'P3',
          obb: obbAt(f.cx, f.cy, f.hw, f.hh, 0),
        };
        occ.add(forced);
        elements.push(forced);
        degradations.push(`seat${s.seat}.pill:forced`);
      } else {
        // 纯 chip（无行动）无可见性契约，沿用收敛隐藏。
        degradations.push(`${id}:hidden`);
      }
    }
  }

  return { box, density, elements, degradations };
}

/**
 * `validateRing → validateClusters`（设计 §4.3 · S2-5）：把「只校验 pod」升级为「校验全簇 OBB」。
 * 复用阶段1 的 `assertNoOverlap`（全卫星 OBB/SAT + 白名单 + 中心带/越界区域约束）；若传入 `ringInput`
 * 则叠加 `validateRing`（pod 越界/pod↔pod/侵入我区/中心带/浮层）。**同源同白名单，阶段2 不引入第二套判定。**
 */
export function validateClusters(
  plan: ClusterPlan,
  inp: OverlayInput,
  ringInput: RingInput | null = null,
  policy: LayoutPolicy = LAYOUT_POLICY_DEFAULT,
  attributionPolicy: AttributionPolicy = ATTRIBUTION_POLICY_DEFAULT,
): RingViolationCheck {
  const snap = clusterPlanToSnapshot(plan, inp);
  const noOverlap = assertNoOverlap(snap, policy);
  // M2 归属硬约束：主路径快照直接过 assertAttribution（κ ≤ 0.6 / 贴靠上限 / 跨主人语义间距），
  // 与 clusterPlanToSnapshot 落位口径同源，CI 主链在此拦截任何归属歧义。
  const attribution = assertAttribution(snap, attributionPolicy);
  const base = ringInput ? validateRing(plan, ringInput) : { ok: true, violations: [] as string[] };
  return {
    ok: base.ok && noOverlap.ok && attribution.ok,
    violations: [...base.violations, ...noOverlap.violations, ...attribution.violations],
  };
}
